<template>
  <AnalyticsWidget
    title="Production Capacity Summary"
    subTitle="Production capacity summary for production stages"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">
    <div class="p-2 h-100">
      <ReportFilterPopover
        headerTitle="Capacity Summary Filter"
        :showApplyButton="true"
        :showResetButton="true"
        :appendToBody="true"
        alignment="left"
        placement="bottom-end"
        @apply="applyCapacitySummaryFilter"
        @reset="resetCapacitySummaryFilter">
        <template v-slot:popover>
          <ReportFilterGroup label="Date Filter">
            <ReportDateRangeSelect
              :startDate="editingCapacitySummaryFilter.startDate"
              :endDate="editingCapacitySummaryFilter.endDate"
              @updateStartDate="editingCapacitySummaryFilter.startDate = $event"
              @updateEndDate="editingCapacitySummaryFilter.endDate = $event"
            />
          </ReportFilterGroup>
          <ReportFilterGroup label="Percent Display">
            <FormSwitch v-model="editingCapacitySummaryFilter.isPercentDisplay" booleanMode />
          </ReportFilterGroup>
        </template>
        <template v-slot:filterDisplay>
          <div>
            <i class="far fa-calendar mr-2"></i>{{appliedCapacitySummaryFilter.startDate | formatDate}} - {{appliedCapacitySummaryFilter.endDate | formatDate}}
          </div>
          <div>
            <i class="far fa-info-circle mr-2"></i>{{isPercentDisplay ? 'Percent Display' : 'Units Display'}}
          </div>
        </template>
      </ReportFilterPopover>

      <LoadingPanel :loading="loading" />
      <AnalyticsBreakdownTable v-if="!loading">
        <template v-slot:header>
          <tr>
            <th width="10%">Stage</th>
            <th>Lead Time</th>
            <th v-for="(dateInfo, index) in stageSummaryData.dateList" :key="'header-' + index">
              {{formatDateCustom(dateInfo.date, 'ddd Do')}}
            </th>
          </tr>
        </template>
        <template v-slot:body>
          <tr v-for="(stageInfo) in stageList" :key="'stage-' + stageInfo.id">
            <td><LinkProductionStage :name="stageInfo.name" :id="stageInfo.id" /></td>
            <td>
              <Tooltip label="Dynamic lead time calculation based on demand" v-if="stageInfo.leadTimeType == ProductionStageLeadTimeTypes.dynamic" helpStyle>
                <span :class="{'text-danger': stageHasLeadTimeWarning(stageInfo)}">{{stageInfo.leadTime}} mins</span>
              </Tooltip>
              <span v-else>{{stageInfo.leadTime}} mins</span>
            </td>
            <td v-for="(dateInfo, dateIndex) in stageInfo.dateList" :key="'stage-' + stageInfo.id + '-date-' + dateIndex" class="p-2">
              <AnalyticsProgressBar :class="[dateInfo.displayClass, 'capacity-progress-bar-clickable']" :value="dateInfo.percent" @click="barClicked($event, stageInfo, dateInfo)">
                <UOMValue :value="dateInfo.displayUnits" :uomType="stageInfo.uomType" :precision="2" v-if="!isPercentDisplay" />
                <span v-if="isPercentDisplay">{{formatPercent(dateInfo.capacityPercent, 0)}}</span>
              </AnalyticsProgressBar>
            </td>
          </tr>
        </template>
      </AnalyticsBreakdownTable>
    </div>

    <Popover ref="detailPopover" sizeClass="popover-xlarge" :showHeader="true" headerTitle="Stage Summary" placement="right">
      <div class="p-2">
        <div class="row" v-if="selectedStageInfo && selectedDateInfo">
          <div class="col-6">
            <PopoverPropertyRow label="Stage">{{selectedStageInfo.name}}</PopoverPropertyRow>
            <PopoverPropertyRow label="Date">{{formatDate(selectedDateInfo.date)}}</PopoverPropertyRow>
            <PopoverPropertyRow label="Available Minutes">{{selectedDateInfo.availableMinutes}} mins</PopoverPropertyRow>
            <PopoverPropertyRow label="Total Capacity / Hr">{{formatDecimal(selectedDateInfo.totalCapacityUnitsPerHour, 2)}} / {{formatLookupName(selectedStageInfo.uomType, UOMTypeLookup)}}</PopoverPropertyRow>
            <PopoverPropertyRow label="Total Day Capacity">{{formatDecimal(selectedDateInfo.totalCapacityUnits, 2)}} / {{formatLookupName(selectedStageInfo.uomType, UOMTypeLookup)}}</PopoverPropertyRow>
          </div>
          <div class="col-6">
            <PopoverPropertyRow label="Units Due"><UOMValue :value="selectedDateInfo.totalUnitsDue" :uomType="selectedStageInfo.uomType" :precision="2" /></PopoverPropertyRow>
            <PopoverPropertyRow label="Units Completed"><UOMValue :value="selectedDateInfo.totalUnitsCompleted" :uomType="selectedStageInfo.uomType" :precision="2" /></PopoverPropertyRow>
            <PopoverPropertyRow label="Total Units"><UOMValue :value="selectedDateInfo.totalUnitsDue + selectedDateInfo.totalUnitsCompleted" :uomType="selectedStageInfo.uomType" :precision="2" /></PopoverPropertyRow>
            <PopoverPropertyRow label="Capacity">{{formatPercent(selectedDateInfo.capacityPercent)}}</PopoverPropertyRow>
            
            <PopoverPropertyRow label="Panels Completed">{{selectedDateInfo.totalTasksCompleted}}</PopoverPropertyRow>
            <PopoverPropertyRow label="Avg Time Per Completed Panel" v-if="selectedDateInfo.totalTasksCompleted > 0">
              {{formatDecimal(selectedDateInfo.availableMinutes / selectedDateInfo.totalTasksCompleted, 2)}} mins
            </PopoverPropertyRow>
            <PopoverPropertyRow label="Avg Time Per Completed Unit" v-if="selectedDateInfo.totalUnitsCompleted > 0">
              {{formatDecimal(selectedDateInfo.availableMinutes / selectedDateInfo.totalUnitsCompleted, 2)}} mins
            </PopoverPropertyRow>
          </div>
        </div>
      </div>
    </Popover>
  </AnalyticsWidget>
</template>

<style lang="scss" scoped>
.capacity-progress-bar-clickable {
  cursor: pointer;
}
</style>


<script>
import _ from 'lodash'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsBreakdownTable from '@/modules/intelligence/components/analyticsBreakdownTable'
import AnalyticsBreakdownValueCell from '@/modules/intelligence/components/analyticsBreakdownValueCell'
import AnalyticsProgressBar from '@/modules/intelligence/components/analyticsProgressBar'

import Popover from '@/components/popover'
import PopoverPropertyRow from '@/components/popoverPropertyRow'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

import { UOMTypeLookup, ProductionStageLeadTimeTypes, ProductionStageLeadTimeTypeLookup } from '@/store/types'

export default {
  name: 'ProductionCapacitySummaryWidget',
  components: {
    AnalyticsWidget,
    AnalyticsBreakdownTable,
    AnalyticsBreakdownValueCell,
    AnalyticsProgressBar,
    Popover,
    PopoverPropertyRow,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup
  },
  props: {
    filter: Object,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  computed: {
    isPercentDisplay() {
      return this.appliedCapacitySummaryFilter.isPercentDisplay
    }
    
  },
  data() {
    return {
      loading: true,
      stageSummaryData: {},
      stageList: [],

      defaultCapacitySummaryFilter: {    
        startDate: moment().local().startOf('day').toDate().toISOString(),
        endDate: moment().local().add(7, 'days').endOf('day').toDate().toISOString(),
        isPercentDisplay: false
      },
      editingCapacitySummaryFilter: {},
      appliedCapacitySummaryFilter: {},

      selectedStageInfo: null,
      selectedDateInfo: null
    }
  },
  watch: {
    filter: function() {
      this.requestData()
    }
  },
  created() {
    this.UOMTypeLookup = UOMTypeLookup
    this.ProductionStageLeadTimeTypes = ProductionStageLeadTimeTypes
    this.ProductionStageLeadTimeTypeLookup = ProductionStageLeadTimeTypeLookup
  },
  mounted() {
    this.editingCapacitySummaryFilter = _.clone(this.defaultCapacitySummaryFilter)
    this.appliedCapacitySummaryFilter = _.clone(this.defaultCapacitySummaryFilter)
    
    this.loading = true
    this.requestData()
  },
  beforeDestroy() {
  },
  methods: {
    requestData() {
      var params = {
        startDate: this.appliedCapacitySummaryFilter.startDate,
        endDate: this.appliedCapacitySummaryFilter.endDate,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null,
      }

      this.$apiService.analyticsService.getProductionStageCapacitySummary(params)
      .then(response => {
        this.stageSummaryData = _.cloneDeep(response.data)

        this.stageList = _.compact(this.stageSummaryData.productionStageList.map(stage => {
          let stageDateList = []

          stageDateList = this.stageSummaryData.dateList.map(dateInfo => {
            let stageDateList = dateInfo.stageList.filter(stageData => stageData.productionStageId == stage.id)
           
            let outputDateInfo = {
              availableMinutes: dateInfo.availableMinutes,
              date: dateInfo.date,
              percent: 0,
              displayClass: '',
              displayUnits: 0,
              totalCapacityUnitsPerHour: stage.totalCapacityUnitsPerHour,
              totalCapacityUnits: (dateInfo.availableMinutes / 60.0) * stage.totalCapacityUnitsPerHour,
              stageTaskCount: stageDateList.reduce((sum, stageData) => sum + stageData.stageTaskCount, 0),
              totalUnitsDue: stageDateList.reduce((sum, stageData) => sum + stageData.totalUnitsDue, 0),
              totalUnitsCompleted: stageDateList.reduce((sum, stageData) => sum + stageData.totalUnitsCompleted, 0),
              totalTasksCompleted: stageDateList.reduce((sum, stageData) => sum + stageData.totalTasksCompleted, 0)
            }

            // Calculate percent & units completed + due
            outputDateInfo.displayUnits = outputDateInfo.totalUnitsDue + outputDateInfo.totalUnitsCompleted
            if(dateInfo.availableMinutes > 0 && stage.totalCapacityUnitsPerHour > 0) {
              if(outputDateInfo.totalCapacityUnits > 0) {
                let capacityPercent = (outputDateInfo.displayUnits / outputDateInfo.totalCapacityUnits) * 100
                outputDateInfo.capacityPercent = capacityPercent
                outputDateInfo.percent = Math.min(100, capacityPercent)

                if(capacityPercent > 0)
                  outputDateInfo.displayClass = 'analytics-progress-bar-good'
                if(capacityPercent > 80)
                  outputDateInfo.displayClass = 'analytics-progress-bar-warning'
                if(capacityPercent > 100)
                  outputDateInfo.displayClass = 'analytics-progress-bar-bad'
              }
            }

            return outputDateInfo
          })

          // If no data for this stage, skip it
          if(stageDateList.every(dateInfo => dateInfo.stageTaskCount == 0))
            return null

          return {
            id: stage.id,
            name: stage.name,
            uomType: stage.stationUoMType,
            leadTimeType: stage.leadTimeType,
            leadTime: stage.leadTime,
            dynamicLeadTimeWarningThreshold: stage.dynamicLeadTimeWarningThreshold,
            totalCapacityUnitsPerHour: stage.totalCapacityUnitsPerHour,
            dateList: stageDateList
          }
        }))
      })
      .finally(() => {
        this.loading = false
      })
      .catch(err => {
      })
    },
    reloadData() {
      this.requestData()
    },
    applyCapacitySummaryFilter() {
      this.appliedCapacitySummaryFilter = _.clone(this.editingCapacitySummaryFilter)

      this.reloadData()
    },
    resetCapacitySummaryFilter() {
      this.editingCapacitySummaryFilter = _.clone(this.defaultCapacitySummaryFilter)
      this.appliedCapacitySummaryFilter = _.clone(this.defaultCapacitySummaryFilter)

      this.reloadData()
    },
    barClicked(ev, stageInfo, dateInfo) {
      this.selectedStageInfo = stageInfo
      this.selectedDateInfo = dateInfo

      this.$refs.detailPopover.displayFromElement(ev.currentTarget)
    },
    stageHasLeadTimeWarning(stageInfo) {
      if(stageInfo.leadTimeType == ProductionStageLeadTimeTypes.dynamic) {
        return stageInfo.dynamicLeadTimeWarningThreshold > 0 && stageInfo.leadTime > stageInfo.dynamicLeadTimeWarningThreshold
      }

      return false
    }
  }
}
</script>